import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  makeStyles, tokens,
  Field, Input, Label, Button, Spinner, Text,
  MessageBar, MessageBarBody,
  Dialog, DialogSurface, DialogBody,
} from '@fluentui/react-components';
import { useAuth } from '../context/AuthContext';
import { API_BASE } from '../config.js';
import { Heading } from '../theme/primitives';
import { iconMap } from '../theme/icons';

const CloseIcon = iconMap.close;
const MailReadIcon = iconMap.mark_email_read;

// Tamsi pusiau permatoma uždanga ir "apple" tipo panelės šešėlis/spindulys yra
// sąmoningi dekoratyvūs sprendimai (ne semantinės Fluent spalvos/token'ai), tad
// lieka literalios CSS reikšmės — likusios (tarpai, šriftai, spalvos) naudoja
// Fluent token'us, ta pati logika kaip src/layout/NavigationBar.jsx.
const useStyles = makeStyles({
  // Realus Fluent <Dialog>/<DialogSurface> vietoj rankinio position:fixed
  // uždangos+backdrop+panelės (žr. /admin/bugs "Reikia sulygiuoti, sutvarkyti
  // sita dialog langa (ir kitus) kad butu standartinio dizaino") — Fluent pats
  // suteikia focus trap'ą, Escape-uždarymą ir standartinį backdrop'ą (anksčiau
  // rankinis variantas Escape klavišo apskritai neapdorodavo). Vizuali tapatybė
  // (suapvalinti kampai, šešėlis, plotis, scale-in animacija) išlaikyta per šią
  // className ant DialogSurface.
  // 26rem — kompaktiškas, prisijungimo langui tinkamas plotis (žr. /admin/bugs
  // "Sumazinti sia dialog langa..."). Anksčiau čia buvo 37.5rem (tas pats
  // platus standartas, kaip daugiapakopiai konfigūratoriaus pasirinkimo
  // dialogai, pvz. SelectTypeDialog.jsx) — vienam trumpam el.pašto+slaptažodžio
  // formos langui tai atrodė nepagrįstai plati.
  surface: {
    borderRadius: tokens.borderRadiusXLarge,
    boxShadow: '0 32px 80px rgba(0,0,0,0.16), 0 8px 24px rgba(0,0,0,0.08)',
    maxWidth: '26rem',
    width: '100%',
    padding: 0,
  },
  // Paprastas subtle X, BE shape="circular"/pilko apskrito fono — ta pati
  // išvaizda, kokią konfigūratoriaus dialogai (pvz. SkirtingDialog.jsx) gauna
  // per DialogTitle's `action` (žr. /admin/bugs "pakeisti X i standartini koki
  // pvz naudojam konfiguratoriaus puslapyje").
  closeBtn: {
    position: 'absolute', top: tokens.spacingVerticalL, right: tokens.spacingHorizontalL,
  },
  // fui-DialogBody yra VIDINIS CSS grid (fiksuoto pločio takeliai, žr. tą pačią
  // problemą SelectTypeDialog.jsx .actions komentare) — be gridColumn:'1/-1' šis
  // div'as užimdavo TIK VIENĄ grid takelį, tad turinys likdavo suspaustas kairėje,
  // neužpildydamas viso naujo, platesnio dialogo pločio.
  content: {
    position: 'relative',
    gridColumn: '1 / -1',
    paddingLeft: tokens.spacingHorizontalXXXL, paddingRight: tokens.spacingHorizontalXXXL,
    paddingTop: tokens.spacingVerticalXXXL, paddingBottom: tokens.spacingVerticalXXXL,
  },
  iconAvatar: {
    width: '48px', height: '48px',
    backgroundColor: tokens.colorBrandBackground,
    borderRadius: tokens.borderRadius3XLarge,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    marginLeft: 'auto', marginRight: 'auto', marginBottom: tokens.spacingVerticalXXL,
    boxShadow: '0 4px 12px rgba(9,170,137,0.4)',
    color: tokens.colorNeutralForegroundOnBrand,
    fontSize: '24px',
  },
  heading: { textAlign: 'center', marginBottom: tokens.spacingVerticalXS },
  subtitle: {
    textAlign: 'center', marginBottom: tokens.spacingVerticalXXXL,
    color: tokens.colorNeutralForeground3,
  },
  messageBar: { marginBottom: tokens.spacingVerticalXL },
  form: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL },
  otpInput: {
    textAlign: 'center', fontSize: tokens.fontSizeBase600,
    letterSpacing: '0.5em', fontFamily: tokens.fontFamilyMonospace,
  },
  submitBtn: { width: '100%', marginTop: tokens.spacingVerticalS },
  mutedLinkBtn: {
    width: '100%', color: tokens.colorNeutralForeground3,
    ':hover': { color: tokens.colorNeutralForeground1 },
  },
  brandLinkBtnBlock: {
    width: '100%', fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorCompoundBrandForeground1,
    ':hover': { color: tokens.colorCompoundBrandForeground1Hover },
  },
  brandLinkBtn: {
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorCompoundBrandForeground1,
    ':hover': { color: tokens.colorCompoundBrandForeground1Hover },
  },
  forgotBtn: {
    minWidth: 0, padding: 0, height: 'auto',
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorCompoundBrandForeground1,
    ':hover': { color: tokens.colorCompoundBrandForeground1Hover },
  },
  passwordLabelRow: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    marginBottom: tokens.spacingVerticalXS,
  },
  // Bare <Input> (be <Field> apgaubimo, žr. žemiau) NEGAUNA Field'o taikomo
  // block+100% plotinimo, tad likdavo tik prigimtinio turinio pločio — kitaip
  // nei email laukas aukščiau (jis TAIP apgaubtas <Field>) (žr. /admin/bugs
  // "slaptazodzio input turi buti per visa ploti").
  passwordInput: { width: '100%' },
  centerBlock: {
    textAlign: 'center', display: 'flex', flexDirection: 'column',
    alignItems: 'center', gap: tokens.spacingVerticalL,
  },
  confirmIconWrap: {
    width: '56px', height: '56px',
    backgroundColor: tokens.colorBrandBackground2,
    borderRadius: tokens.borderRadiusCircular,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    color: tokens.colorBrandForeground2,
    fontSize: '32px',
  },
  confirmText: {
    color: tokens.colorNeutralForeground3,
    lineHeight: tokens.lineHeightBase300,
  },
  switchRow: {
    marginTop: tokens.spacingVerticalXXL, paddingTop: tokens.spacingVerticalXXL,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    textAlign: 'center',
  },
  switchText: { color: tokens.colorNeutralForeground3 },
});

function Login({ open, onClose }) {
  const s = useStyles();
  const { t } = useTranslation();
  const { login, loginWithData, register } = useAuth();
  const [isRegistering, setIsRegistering] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [registerData, setRegisterData] = useState({ name: '', email: '', password: '', confirmPassword: '' });
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  // 2FA state
  const [twoFactorEmail, setTwoFactorEmail] = useState(null);
  const [otp, setOtp] = useState('');

  // Forgot password state
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSent, setForgotSent] = useState(false);

  useEffect(() => {
    if (open) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  const reset = () => {
    setError(null); setSuccess(''); setEmail(''); setPassword(''); setOtp('');
    setTwoFactorEmail(null);
    setForgotMode(false); setForgotEmail(''); setForgotSent(false);
    setRegisterData({ name: '', email: '', password: '', confirmPassword: '' });
  };

  const handleClose = () => { onClose(); reset(); setIsRegistering(false); };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError(null); setLoading(true);
    try {
      const data = await login(email, password);
      if (data.twoFactorRequired) {
        setTwoFactorEmail(data.email);
        setSuccess(t('login_modal.otp_sent'));
      } else {
        handleClose();
      }
    }
    catch (err) { setError(err.message || t('login_modal.login_error')); }
    finally { setLoading(false); }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setError(null); setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/auth/2fa/verify`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: twoFactorEmail, otp }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || t('login_modal.wrong_code'));
      loginWithData(data.user);
      handleClose();
    }
    catch (err) { setError(err.message || t('login_modal.wrong_code')); }
    finally { setLoading(false); }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (registerData.password !== registerData.confirmPassword) { setError(t('login_modal.password_mismatch')); return; }
    if (registerData.password.length < 6) { setError(t('login_modal.password_too_short')); return; }
    setError(null); setLoading(true);
    try {
      await register(registerData.name, registerData.email, registerData.password);
      setSuccess(t('login_modal.register_success'));
      setTimeout(() => { setIsRegistering(false); setSuccess(''); }, 1800);
    } catch (err) { setError(err.message || t('login_modal.register_error')); }
    finally { setLoading(false); }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError(null); setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotEmail }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || t('login_modal.generic_error'));
      setForgotSent(true);
    } catch (err) {
      setError(err.message || t('login_modal.server_error'));
    } finally {
      setLoading(false);
    }
  };

  // Heading and subtitle text
  const getHeading = () => {
    if (twoFactorEmail) return t('login_modal.heading_2fa');
    if (forgotMode) return t('login_modal.heading_forgot');
    if (isRegistering) return t('login_modal.heading_register');
    return t('login_modal.heading_login');
  };
  const getSubtitle = () => {
    if (twoFactorEmail) return t('login_modal.subtitle_2fa', { email: twoFactorEmail });
    if (forgotMode) return t('login_modal.subtitle_forgot');
    if (isRegistering) return t('login_modal.subtitle_register');
    return t('login_modal.subtitle_login');
  };
  const getIcon = () => {
    if (twoFactorEmail) return 'shield';
    if (forgotMode) return 'lock_reset';
    return 'chair';
  };

  const AvatarIcon = iconMap[getIcon()];

  return (
    <Dialog open={open} onOpenChange={(_e, data) => { if (!data.open) handleClose(); }}>
      <DialogSurface className={s.surface}>
        <DialogBody>
        <div className={s.content}>
          <Button
            appearance="subtle"
            aria-label={t('topbar.close')}
            icon={<CloseIcon />}
            onClick={handleClose}
            className={s.closeBtn}
          />
          <div className={s.iconAvatar}>
            <AvatarIcon />
          </div>

          <Heading as="h2" variant="h2" className={s.heading}>{getHeading()}</Heading>
          <Text as="p" size={300} className={s.subtitle}>{getSubtitle()}</Text>

          {success && (
            <MessageBar intent="success" className={s.messageBar}>
              <MessageBarBody>{success}</MessageBarBody>
            </MessageBar>
          )}
          {error && (
            <MessageBar intent="error" className={s.messageBar}>
              <MessageBarBody>{error}</MessageBarBody>
            </MessageBar>
          )}

          {/* ── 2FA OTP ── */}
          {twoFactorEmail ? (
            <form onSubmit={handleVerifyOtp} className={s.form}>
              <Field label={t('login_modal.otp_label')}>
                <Input
                  input={{ className: s.otpInput }}
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={otp}
                  onChange={e => setOtp(e.target.value.replace(/\D/g, ''))}
                  placeholder="000000"
                  required
                  autoFocus
                />
              </Field>
              <Button type="submit" appearance="primary" size="large" disabled={loading || otp.length < 6} className={s.submitBtn}>
                {loading ? <Spinner size="tiny" /> : t('login_modal.confirm')}
              </Button>
              <Button
                type="button"
                appearance="transparent"
                onClick={() => { setTwoFactorEmail(null); setOtp(''); setError(null); setSuccess(''); }}
                className={s.mutedLinkBtn}
              >
                {t('login_modal.back_to_login')}
              </Button>
            </form>

          /* ── Forgot password ── */
          ) : forgotMode ? (
            forgotSent ? (
              <div className={s.centerBlock}>
                <div className={s.confirmIconWrap}>
                  <MailReadIcon />
                </div>
                <Text as="p" size={300} className={s.confirmText}>
                  {t('login_modal.forgot_sent_pre')} <strong>{forgotEmail}</strong> {t('login_modal.forgot_sent_post')}<br />
                  {t('login_modal.forgot_sent_2')}
                </Text>
                <Button
                  appearance="transparent"
                  onClick={() => { setForgotMode(false); setForgotSent(false); setForgotEmail(''); setError(null); }}
                  className={s.brandLinkBtnBlock}
                >
                  {t('login_modal.back_to_login_2')}
                </Button>
              </div>
            ) : (
              <form onSubmit={handleForgotPassword} className={s.form}>
                <Field label={t('login_modal.email_label')}>
                  <Input
                    type="email"
                    value={forgotEmail}
                    onChange={e => setForgotEmail(e.target.value)}
                    placeholder="jonas@pvz.lt"
                    required
                    autoFocus
                  />
                </Field>
                <Button type="submit" appearance="primary" size="large" disabled={loading} className={s.submitBtn}>
                  {loading ? <Spinner size="tiny" /> : t('login_modal.send_link')}
                </Button>
                <Button
                  type="button"
                  appearance="transparent"
                  onClick={() => { setForgotMode(false); setError(null); }}
                  className={s.mutedLinkBtn}
                >
                  {t('login_modal.back_to_login')}
                </Button>
              </form>
            )

          /* ── Register ── */
          ) : isRegistering ? (
            <form onSubmit={handleRegister} className={s.form}>
              <Field label={t('login_modal.name_label')}>
                <Input
                  name="name" value={registerData.name}
                  onChange={e => setRegisterData(p => ({ ...p, name: e.target.value }))}
                  placeholder="Jonas Jonaitis" required
                />
              </Field>
              <Field label={t('login_modal.email_label')}>
                <Input
                  name="email" type="email" value={registerData.email}
                  onChange={e => setRegisterData(p => ({ ...p, email: e.target.value }))}
                  placeholder="jonas@pvz.lt" required
                />
              </Field>
              <Field label={t('login_modal.password_label')}>
                <Input
                  name="password" type="password" value={registerData.password}
                  onChange={e => setRegisterData(p => ({ ...p, password: e.target.value }))}
                  placeholder={t('login_modal.min_6_chars')} required
                />
              </Field>
              <Field label={t('login_modal.confirm_password_label')}>
                <Input
                  name="confirmPassword" type="password" value={registerData.confirmPassword}
                  onChange={e => setRegisterData(p => ({ ...p, confirmPassword: e.target.value }))}
                  placeholder={t('login_modal.repeat_password_ph')} required
                />
              </Field>
              <Button type="submit" appearance="primary" size="large" disabled={loading} className={s.submitBtn}>
                {loading ? <Spinner size="tiny" /> : t('login_modal.register_btn')}
              </Button>
            </form>

          /* ── Login ── */
          ) : (
            <form onSubmit={handleLogin} className={s.form}>
              <Field label={t('login_modal.email_label')}>
                <Input
                  type="email" value={email}
                  onChange={e => setEmail(e.target.value)} placeholder="jonas@pvz.lt" required
                />
              </Field>
              <div>
                <div className={s.passwordLabelRow}>
                  <Label htmlFor="login-password-input">{t('login_modal.password_label')}</Label>
                  <Button
                    type="button"
                    appearance="transparent"
                    className={s.forgotBtn}
                    onClick={() => { setForgotMode(true); setForgotEmail(email); setError(null); }}
                  >
                    {t('login_modal.forgot_password')}
                  </Button>
                </div>
                <Input
                  id="login-password-input"
                  className={s.passwordInput}
                  type="password" value={password}
                  onChange={e => setPassword(e.target.value)} placeholder="••••••••" required
                />
              </div>
              <Button type="submit" appearance="primary" size="large" disabled={loading} className={s.submitBtn}>
                {loading ? <Spinner size="tiny" /> : t('login_modal.login_btn')}
              </Button>
            </form>
          )}

          {!twoFactorEmail && !forgotMode && (
            <div className={s.switchRow}>
              <span className={s.switchText}>
                {isRegistering ? t('login_modal.have_account') : t('login_modal.no_account')}
              </span>
              <Button
                appearance="transparent"
                onClick={() => { reset(); setIsRegistering(!isRegistering); }}
                className={s.brandLinkBtn}
              >
                {isRegistering ? t('login_modal.login_btn') : t('login_modal.create_account')}
              </Button>
            </div>
          )}
        </div>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

export default Login;
